import { create } from "zustand";
import { persist } from "zustand/middleware";
import useFavoriteStore from "./FavoriteStore";
import usePersonaStore from "./PersonaStore";
import { googleLogout } from "@react-oauth/google";

const useAuthStore = create(
  persist(
    (set) => ({
      isAuthenticated: false,
      email: null,
      token: null,
      signInTime: null,
      showOnboarding: false,
      userData: null,
      setShowOnboarding: (value) => set({ showOnboarding: value }),
      setUserData: (data) => set({ userData: data }),
      login: async (email, token, isNewUser = false, userData = null) => {
        // Sync FavoriteStore's loggedIn state
        useFavoriteStore.getState().setLoggedIn(true);
        set({ isAuthenticated: true, email, token, signInTime: Date.now(), showOnboarding: isNewUser, userData });

        // Sync cart to backend after login
        const { default: useCartStore } = await import("./CartStore.jsx");
        await useCartStore.getState().syncCart();
        // Optionally fetch from backend to ensure state is in sync
        await useCartStore.getState().fetchCart();
      },
      logout: async () => {
        localStorage.setItem("prompt", "");

        // Clear event and space data from localStorage
        localStorage.removeItem("eventData");
        localStorage.removeItem("spaceData");

        // Clear FavoriteStore state and sync loggedIn status
        useFavoriteStore.setState({ events: [], spaces: [] });
        useFavoriteStore.getState().setLoggedIn(false);

        // Reset CartStore on logout
        const { default: useCartStore } = await import("./CartStore.jsx");
        useCartStore.getState().clearCart();

        // Get current persona state before resetting
        const personaStore = usePersonaStore.getState();
        const hasGeneratedPersona = personaStore.generatedPersonaData && personaStore.personaName;

        // Clear any stored search data for unauthenticated users
        localStorage.removeItem("unauthUserSearchText");
        localStorage.removeItem("generatedPersonaData");
        // NOTE: We don't clear pendingPersonaData here because it's needed for persona creation after login
        // pendingPersonaData will be cleared after persona creation in PostAuthPersonaHandler

        // Reset persona store on logout
        personaStore.resetPersonaState();

        // If there was a generated persona, show the create persona component for unauth users
        if (hasGeneratedPersona) {
          usePersonaStore.setState({
            showCreatePersonaForUnauthUser: true,
            generatedPersonaData: personaStore.generatedPersonaData,
            personaName: personaStore.personaName,
          });
        }

        // Additional cleanup
        localStorage.removeItem("token");
        localStorage.removeItem("loggedin");
        googleLogout();

        // Dynamic import to avoid circular dependency
        const { default: useAppStore } = await import("./SearchStore.jsx");
        useAppStore.getState().setSearchText("");

        set({ isAuthenticated: false, email: null, token: null, signInTime: null });
      },
    }),
    {
      name: "auth-storage", // name of the item in localStorage
    }
  )
);

export default useAuthStore;